import type { CSSProperties } from "react";
import type { ProductCategory, ProductUnit } from "@/features/product/types";
import type { UnitType } from "@/features/quotation/types";
import {
  isDirectKgUnit,
  usesKgPerBox,
  usesUnitsPerBox,
} from "@/features/quotation/unitLabels";
import { parseMoneyInput } from "@/lib/timeAgo";

export function getQuotationItemValidationError({
  unitType,
  pricePerBox,
  pricePerKg,
  kgPerBox,
  unitsPerBox,
}: {
  unitType: UnitType;
  pricePerBox: string;
  pricePerKg: string;
  kgPerBox: string;
  unitsPerBox: string;
}): string | null {
  const price = parseMoneyInput(
    isDirectKgUnit(unitType) ? pricePerKg : pricePerBox,
  );
  if (price == null || price <= 0) return "Informe um preço maior que zero.";
  if (usesKgPerBox(unitType) && !(Number(kgPerBox) > 0))
    return "Informe kg por caixa maior que zero.";
  if (usesUnitsPerBox(unitType) && !(Number(unitsPerBox) > 0))
    return "Informe unidades por caixa maiores que zero.";
  return null;
}

export const EXIT_ANIMATION_MS = 220;

export const CATEGORY_OPTIONS: ProductCategory[] = [
  "LEGUMES",
  "FRUTAS",
  "VERDURAS",
  "GRAOS",
  "TEMPEROS",
  "OVOS",
  "OUTROS",
];

export const UNIT_OPTIONS: ProductUnit[] = [
  "kg",
  "cx_kg",
  "cx_un",
  "bdj",
  "un",
  "sc_kg",
];

export const UNIT_OPTION_LABELS: Record<ProductUnit, string> = {
  kg: "Kg",
  cx_kg: "Cx/Kg",
  cx_un: "Cx/Un",
  bdj: "Bandeja",
  un: "Un",
  sc_kg: "Sc/Kg",
};

export function mapProductDefaultUnitToQuotationUnit(
  unit: ProductUnit | "KG" | "UNIDADE" | "CAIXA",
): UnitType {
  switch (unit) {
    case "kg":
    case "cx_un":
    case "bdj":
    case "un":
    case "sc_kg":
    case "cx_kg":
      return unit;
    case "CAIXA":
      return "cx_un";
    case "UNIDADE":
      return "un";
    case "KG":
    default:
      return "kg";
  }
}

export const inputStyle: CSSProperties = {
  width: "100%",
  padding: "0.625rem 0.75rem",
  background: "var(--login-bg)",
  border: "1px solid var(--login-border)",
  borderRadius: "0.5rem",
  color: "var(--login-text)",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.9375rem",
  outline: "none",
  boxSizing: "border-box",
};

export const selectStyle: CSSProperties = {
  ...inputStyle,
  appearance: "none",
  paddingRight: "2.25rem",
  backgroundImage: `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 0.875rem center",
  backgroundSize: "0.75rem",
};

export const labelStyle: CSSProperties = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 600,
  fontSize: "0.75rem",
  letterSpacing: "0.06em",
  color: "var(--login-muted)",
};

export const btnPrimary: CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  background: "var(--login-accent)",
  border: "none",
  borderRadius: "0.5rem",
  color: "#fff",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "1rem",
  letterSpacing: "0.06em",
  cursor: "pointer",
};

export const btnDanger: CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  border: "1px solid var(--login-error, #e05353)",
  borderRadius: "0.5rem",
  color: "var(--login-error, #e05353)",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize: "1rem",
  letterSpacing: "0.06em",
  cursor: "pointer",
  background: "transparent",
};
